import {faArrowRightArrowLeft, faDeleteLeft, faPlus} from '@fortawesome/free-solid-svg-icons'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {Field, Formik} from 'formik'
import React, {FC, useEffect, useRef, useState} from 'react'
import {Form} from 'react-bootstrap'
import {PlatformSettingDataTypesValues} from 'src/app/_interfaces/setting.interface'
import useAppToast from 'src/hooks/useAppToast'
import {
  FIELD_MAPPING_DATA,
  FIELD_MAPPING_TYPE,
  PLATFORM_FIELD,
  SECTION_WISE_SAVED_FIELD_MAPPING,
  ShippingMethodMapping,
  platformfielddata_set,
} from 'src/interfaces/Platform.interface'
import {deleteFieldMappingAsync, getPlatformFieldsAsync} from 'src/redux/actions/productActions'
import {getPlatformParentFieldDataCategoriesAsync} from 'src/redux/actions/templateAndPricingActions'
import {useAppDispatch} from 'src/redux/storeHooks'
import Loader from 'src/utils/Loader'
import {getSavedFieldMappingsDataAsyncByMappingId} from 'src/redux/actions/platformSettingActions'
import clsx from 'clsx'
import {getSequenceMappingInputWidthClass} from 'src/utils/channelSettingSequenceLayout'
import InfoTooltip from '../common/InfoTooltip'
import PlatformFieldDataSearchSelect, {
  appendPlatformFieldData,
  buildPlatformSearchField,
  resolveSavedDestinationPlatformDataId,
  toPlatformFieldMultiSelectValue,
  toPlatformFieldSelectValue,
} from 'src/app/pages/listing/components/PlatformFieldDataSearchSelect'
import { usePermission } from 'src/hooks/usePermission'
import MappingShortcodeChips from 'src/app/pages/listing/components/MappingShortcodeChips'
import {
  resolveFieldMappingShortcodeSettingName,
  shouldShowFieldMappingShortcodes,
} from 'src/utils/platformSettingShortcodeUtils'
import {useApplyPlatformSettingShortcodes} from 'src/utils/usePlatformSettingDisplayText'

const mergeSavedRowsIntoFieldData = (
  platformData: platformfielddata_set[] = [],
  savedRows: any[] = []
): platformfielddata_set[] => {
  let next = [...platformData]
  savedRows.forEach((row) => {
    const saved = row?.destination_platform_data
    if (saved?.id) {
      next = appendPlatformFieldData(next, saved)
    }
  })
  return next
}

const MappingInput: FC<{
  sourceSide?: number
  destinationSide?: number
  selectedPlatformId: number
  isMappingVisible: boolean
  handleChange: (e: React.ChangeEvent<any>) => void
  field_mapping: FIELD_MAPPING_TYPE
  savedMapping: SECTION_WISE_SAVED_FIELD_MAPPING | null | undefined
  settingName: string
  settingId: number | null
  setFieldMappings: React.Dispatch<React.SetStateAction<FIELD_MAPPING_DATA[]>>
  fieldMappings: FIELD_MAPPING_DATA[]
  sourcePlatform?: string
  destinationPlatform?: string
  fullWidth?: boolean
  wrapperClassName?: string
}> = ({
  field_mapping,
  isMappingVisible,
  sourceSide,
  destinationSide,
  selectedPlatformId,
  savedMapping,
  settingId,
  settingName,
  setFieldMappings,
  fieldMappings,
  sourcePlatform,
  destinationPlatform,
  fullWidth = false,
  wrapperClassName = 'row mb-2',
}) => {
    const {isRead, isWrite, isDelete} = usePermission();
  const formatSettingDisplayText = useApplyPlatformSettingShortcodes()

  const createEmptyShippingMethodMapping = (): ShippingMethodMapping => ({
    id: null,
    source_platform_data_id: '',
    destination_platform_data_id: '',
    destination_platform_data_str: '',
    source_platform_data_str: '',
    hasPair: false,
  })

  const createClearedMapping = (mappingId?: number | null): ShippingMethodMapping => ({
    id: mappingId ?? null,
    source_platform_data_id: null,
    destination_platform_data_id: null,
    destination_platform_data_str: '',
    source_platform_data_str: '',
    hasPair: false,
  })

  const [platformFields, setPlatformFields] = useState<{
    sourcePlatformField: null | PLATFORM_FIELD
    destinationPlatformField: null | PLATFORM_FIELD
  }>({
    sourcePlatformField: null,
    destinationPlatformField: null,
  })
  const [shipmentMappingFieldData, setShipmentMappingFieldData] = useState<{
    destinationPlatformFieldData: platformfielddata_set[] | []
    sourcePlatformFieldData: platformfielddata_set[] | []
  }>({sourcePlatformFieldData: [], destinationPlatformFieldData: []})

  const [shippingMethodMappings, setShippingMethodMappings] = useState<ShippingMethodMapping[]>([
    createEmptyShippingMethodMapping(),
  ])

  const [loadingMappingFields, setLoadingMappingFields] = useState(true)
  const [loadingShippingMappingFieldData, setLoadingShippingMappingFieldData] = useState(true)
  const dispatch = useAppDispatch()
  const {successToast} = useAppToast()
  const [errors, setErrors] = useState<Record<string, string>>({});
  const platformFieldsFetchRef = useRef('')
  const appliedSavedMappingRef = useRef<number | null>(null)

  // Default (single-side) fields such as ORDER_STATUS / CANCEL_ORDER_STATUS must be
  // persisted with the same contract the backend expects for default mappings:
  // isDefault, the mapping-config setting_mapping_id, and the selected id mirrored into
  // destination_platform_data_str. Without these the backend stores rows with a null
  // destination_platform_data (values never reload) and can overwrite sibling default
  // mappings that share the same order_setting.
  const isDefaultField = Boolean(field_mapping?.isDefault)

  const getDefaultMappingEntryExtras = () =>
    isDefaultField
      ? {
          isDefault: field_mapping?.isDefault,
          field_mapping_id: savedMapping?.id ?? null,
          setting_mapping_id: field_mapping?.setting_mapping_id ?? field_mapping?.id ?? null,
        }
      : {}

  const decorateDefaultMappingRows = (rows: ShippingMethodMapping[] = []): ShippingMethodMapping[] =>
    isDefaultField
      ? rows.map((row) => {
          if (!row) return row
          const destId = row.destination_platform_data_id
          return {
            ...row,
            destination_platform_data_str:
              destId !== null && destId !== undefined && destId !== ''
                ? String(destId)
                : row.destination_platform_data_str ?? '',
            isDefault: field_mapping?.isDefault,
          }
        })
      : rows

  const appendShipmentFieldData = (
    side: 'source' | 'destination',
    raw: any | any[]
  ) => {
    const items = Array.isArray(raw) ? raw : raw ? [raw] : []
    if (!items.length) {
      return
    }

    setShipmentMappingFieldData((prev) => {
      const key =
        side === 'source' ? 'sourcePlatformFieldData' : 'destinationPlatformFieldData'
      let next = [...(prev[key] ?? [])]
      items.forEach((item) => {
        next = appendPlatformFieldData(next, item)
      })
      return {...prev, [key]: next}
    })
  }

  const upsertFieldMappingEntry = (
    previousMappings: FIELD_MAPPING_DATA[],
    entry: FIELD_MAPPING_DATA
  ) => {
    const index = previousMappings.findIndex(
      (item) =>
        item.groupCode === entry.groupCode &&
        item.fieldCode === entry.fieldCode &&
        item.savedSettingId === entry.savedSettingId
    )

    if (index === -1) {
      return [...previousMappings, entry]
    }

    const existing = previousMappings[index]
    if (
      existing.destinationFieldId === entry.destinationFieldId &&
      existing.sourceFieldId === entry.sourceFieldId &&
      JSON.stringify(existing.fieldMappingDataSet) === JSON.stringify(entry.fieldMappingDataSet)
    ) {
      return previousMappings
    }

    const updated = [...previousMappings]
    updated[index] = {...existing, ...entry}
    return updated
  }

  //for getting saved values
  useEffect(() => {
    const applySavedMappingRows = (rows: any[]) => {
      const mappings: ShippingMethodMapping[] = rows.map((item) => {
        const destinationPlatformDataId = resolveSavedDestinationPlatformDataId(item)
        return {
          id: item?.id,
          destination_platform_data_id: destinationPlatformDataId,
          source_platform_data_id: item?.source_platform_data
            ? item?.source_platform_data?.id + ''
            : null,
          // Keep SELECT ids mirrored into *_str so reload works when API returns
          // destination_platform_data: null after default-mapping saves.
          destination_platform_data_str:
            destinationPlatformDataId || item.destination_platform_data_str || '',
          source_platform_data_str: item.source_platform_data_str ?? '',
        }
      })

      if (
        platformFields.destinationPlatformField?.id &&
        platformFields.sourcePlatformField?.id
      ) {
        if (!field_mapping?.isDefault) {
          const normalizedMappings =
            mappings?.length ? mappings : [createEmptyShippingMethodMapping()]
          setShippingMethodMappings(normalizedMappings)
          setFieldMappings((pre) =>
            upsertFieldMappingEntry(pre, {
              groupCode: settingName,
              fieldCode: field_mapping.field_code,
              savedSettingId: settingId,
              savedMappingId: savedMapping?.id!,
              destinationFieldId: platformFields.destinationPlatformField?.id!,
              sourceFieldId: platformFields.sourcePlatformField?.id!,
              fieldMappingDataSet: normalizedMappings,
            })
          )
        }
      }

      if (
        platformFields.destinationPlatformField?.id &&
        platformFields.sourcePlatformField === null
      ) {
        if (platformFields.destinationPlatformField?.input_type === 'MULTISELECT') {
          const destinationIds = mappings
            .map((item: any) => item.destination_platform_data_id)
            .filter(Boolean)

          const ids = mappings.map((item: any) => item.id).filter(Boolean)
          const mergedData = {
            id: ids ?? null,
            destination_platform_data_id: destinationIds,
            source_platform_data_id: mappings[0]?.source_platform_data_id ?? null,
            destination_platform_data_str: mappings[0]?.destination_platform_data_str ?? '',
            source_platform_data_str: mappings[0]?.source_platform_data_str ?? '',
          }

          setShippingMethodMappings([mergedData])
          setFieldMappings((pre) =>
            upsertFieldMappingEntry(pre, {
              groupCode: settingName,
              fieldCode: field_mapping.field_code,
              savedSettingId: settingId,
              savedMappingId: savedMapping?.id!,
              destinationFieldId: platformFields.destinationPlatformField?.id!,
              sourceFieldId: null,
              fieldMappingDataSet: decorateDefaultMappingRows(mappings),
              ...getDefaultMappingEntryExtras(),
            })
          )
        } else {
          const firstMapping = mappings?.[0] ?? createEmptyShippingMethodMapping()
          setShippingMethodMappings([firstMapping])
          setFieldMappings((pre) =>
            upsertFieldMappingEntry(pre, {
              groupCode: settingName,
              fieldCode: field_mapping.field_code,
              savedSettingId: settingId,
              savedMappingId: savedMapping?.id!,
              destinationFieldId: platformFields.destinationPlatformField?.id!,
              sourceFieldId: null,
              fieldMappingDataSet: decorateDefaultMappingRows([firstMapping]),
              ...getDefaultMappingEntryExtras(),
            })
          )
        }
      }
    }

    if (savedMapping && platformFields.destinationPlatformField?.id) {
      if (appliedSavedMappingRef.current === savedMapping.id) {
        setLoadingMappingFields(false)
        return
      }

      setLoadingMappingFields(true)

      if (savedMapping.fieldmappingdata_set?.length) {
        applySavedMappingRows(savedMapping.fieldmappingdata_set)
        appliedSavedMappingRef.current = savedMapping.id
        setLoadingMappingFields(false)
        return
      }

      dispatch(getSavedFieldMappingsDataAsyncByMappingId({mappingId: savedMapping.id}))
        .then((response) => {
          if (response?.payload?.fieldmappingdata_set?.length) {
            applySavedMappingRows(response.payload.fieldmappingdata_set)
            appliedSavedMappingRef.current = savedMapping.id
          }
        })
        .catch((error) => {
          console.log('unable to get field mappings', error)
        })
        .finally(() => {
          setLoadingMappingFields(false)
        })
    } else {
      setLoadingMappingFields(false)
    }
  }, [
    destinationSide,
    platformFields.destinationPlatformField?.id,
    platformFields.sourcePlatformField?.id,
    savedMapping?.id,
    field_mapping?.field_code,
    field_mapping?.isDefault,
    settingId,
    settingName,
  ])

  //for getting platform fields and fields data
  useEffect(() => {
    if (!destinationSide || !isMappingVisible) {
      setLoadingShippingMappingFieldData(false)
      return
    }

    const fieldCode = field_mapping?.field_code ?? field_mapping?.api_code ?? ''
    const fetchKey = `${destinationSide}|${sourceSide ?? ''}|${fieldCode}|${Boolean(field_mapping?.isDefault)}`

    if (platformFieldsFetchRef.current === fetchKey) {
      setLoadingShippingMappingFieldData(false)
      return
    }

    platformFieldsFetchRef.current = fetchKey
  
    const fetchPlatformFields = async () => {
      setLoadingShippingMappingFieldData(true);
  
      const promises:any = [];
  
      // Fetch source side data only if field_mapping is true
      if (!field_mapping?.isDefault && sourceSide) {
        const sourcePromise = dispatch(
          getPlatformFieldsAsync({
            platformId: Number(sourceSide),
            otherParams: { field_code: field_mapping?.field_code },
          })
        ).then(async (response) => {
          if (response.payload?.length) {
            const sourceField = response.payload[0];
  
            setPlatformFields((prev) => ({
              ...prev,
              sourcePlatformField: field_mapping.isDefault ? null : sourceField,
            }));
  
            // Fetch parent field data
            const parentDataResponse = await dispatch(
              getPlatformParentFieldDataCategoriesAsync({ platform_Id: sourceField.id })
            );
  
            setShipmentMappingFieldData((prev) => ({
              ...prev,
              sourcePlatformFieldData:
                parentDataResponse.payload?.length
                  ? parentDataResponse.payload
                  : sourceField.platformfielddata_set ?? [],
            }));
          }
        });
  
        promises.push(sourcePromise);
      }
  
      // Fetch destination side data
      const destinationFieldParams: Record<string, any> = {
        field_code: field_mapping?.field_code,
      }

      if (field_mapping?.field_code === 'CANCEL_ORDER_STATUS' && sourceSide) {
        destinationFieldParams.related_available_platform = Number(sourceSide)
      }

      const destinationPromise = dispatch(
        getPlatformFieldsAsync({
          platformId: Number(destinationSide),
          otherParams: destinationFieldParams,
        })
      ).then(async (response) => {
        if (response.payload?.length) {
          const destinationField = response.payload[0];

          setPlatformFields((prev) => ({
            ...prev,
            destinationPlatformField: destinationField,
          }));

          // Dispatch getPlatformParentFieldDataCategoriesAsync only for "select" or "multiselect"
          if (["SELECT", "MULTISELECT"].includes(destinationField.input_type)) {
            const parentDataResponse = await dispatch(
              getPlatformParentFieldDataCategoriesAsync({ platform_Id: destinationField.id })
            );
            setShipmentMappingFieldData((prev) => ({
              ...prev,
              destinationPlatformFieldData:
                parentDataResponse.payload?.length
                  ? parentDataResponse.payload
                  : destinationField.platformfielddata_set ?? [],
            }));
          }
        } else if (
          field_mapping?.isDefault &&
          savedMapping?.destination_field?.id
        ) {
          const destinationField = savedMapping.destination_field;
          setPlatformFields((prev) => ({
            ...prev,
            destinationPlatformField: destinationField,
          }));

          if (["SELECT", "MULTISELECT"].includes(destinationField.input_type)) {
            const parentDataResponse = await dispatch(
              getPlatformParentFieldDataCategoriesAsync({ platform_Id: destinationField.id })
            );
            setShipmentMappingFieldData((prev) => ({
              ...prev,
              destinationPlatformFieldData:
                parentDataResponse.payload?.length
                  ? parentDataResponse.payload
                  : destinationField.platformfielddata_set ?? [],
            }));
          }
        }
      });
  
      promises.push(destinationPromise);
  
      // Wait for all promises to complete
      Promise.all(promises).finally(() => {
        setLoadingShippingMappingFieldData(false);
      });
    };
  
    fetchPlatformFields();
  }, [
    destinationSide,
    sourceSide,
    isMappingVisible,
    field_mapping?.field_code,
    field_mapping?.api_code,
    field_mapping?.isDefault,
    savedMapping?.id,
    savedMapping?.destination_field?.id,
    dispatch,
  ]);

  useEffect(() => {
    if (
      !field_mapping?.isDefault ||
      !savedMapping?.destination_field?.id ||
      platformFields.destinationPlatformField?.id
    ) {
      return
    }

    const destinationField = savedMapping.destination_field

    setPlatformFields((prev) => ({
      ...prev,
      destinationPlatformField: destinationField,
    }))

    if (!['SELECT', 'MULTISELECT'].includes(destinationField.input_type)) {
      return
    }

    const hydrateDestinationFieldData = async () => {
      const parentDataResponse = await dispatch(
        getPlatformParentFieldDataCategoriesAsync({platform_Id: destinationField.id})
      )

      setShipmentMappingFieldData((prev) => ({
        ...prev,
        destinationPlatformFieldData:
          parentDataResponse.payload?.length
            ? parentDataResponse.payload
            : destinationField.platformfielddata_set ?? [],
      }))
    }

    hydrateDestinationFieldData()
  }, [
    dispatch,
    field_mapping?.isDefault,
    platformFields.destinationPlatformField?.id,
    savedMapping?.destination_field,
    savedMapping?.id,
  ]);
  
  function formatDateString(dateString:string) {
    // Parse the date string to a Date object
    let date;
    if(dateString){
      if (dateString.endsWith("Z")) {
          let zremovedval = dateString.slice(0, dateString.length-4)
          // If the date string ends with 'Z', it's in UTC
          date = new Date(zremovedval);
      } else {
          // Otherwise, it's in the local time zone
          date = new Date(dateString);
      }
      // Adjust the date to the local time zone if needed
      const localDate = new Date(date.getTime() - (date.getTimezoneOffset() * 60000));
  
      // Format the local date for the datetime-local input
      if (isNaN(localDate.getTime())) {
        return "";
      }
      return localDate.toISOString().slice(0, 16);
    }else{
      return null
    }

  }

  const validateField = (index: number, fieldName: string, value: any, isRequired: boolean) => {
    const fieldKey = `${index}_${fieldName}`;
  
    if (isRequired) {
      if (!value || (Array.isArray(value) && value.length === 0)) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          [fieldKey]: 'This field is required',
        }));
      } else {
        setErrors((prevErrors) => {
          const newErrors = { ...prevErrors };
          delete newErrors[fieldKey];
          return newErrors;
        });
      }
    }
  };
  
  

  if (loadingShippingMappingFieldData || loadingMappingFields) return <Loader />
 
  return (
    <div
      className={clsx(
        wrapperClassName,
        getSequenceMappingInputWidthClass(
          !fullWidth &&
            (platformFields.sourcePlatformField === null || field_mapping.isDefault)
        )
      )}
    >
      <div className='mapping'>
        <div className='p-2 '>
          <div>
          <label className={`form-label ${field_mapping?.isRequired ? 'required' : ''}`}>
          {field_mapping?.isDefault
                ? `Select ${formatSettingDisplayText(field_mapping?.display_name)} from ${destinationPlatform} `
                : platformFields?.sourcePlatformField === null ? `Select ${formatSettingDisplayText(platformFields.destinationPlatformField?.display_name)} from ${destinationPlatform}`
                : `${formatSettingDisplayText(field_mapping.display_name)} (${sourcePlatform}) to  ${formatSettingDisplayText(platformFields.destinationPlatformField?.display_name)} (${destinationPlatform}) mapping`
                }
            </label> {field_mapping?.isDefault && platformFields.destinationPlatformField?.description && <InfoTooltip message={platformFields.destinationPlatformField.description}/>}
          </div>
          {!field_mapping?.isDefault && platformFields?.sourcePlatformField !== null && <div className='border-bottom my-2 border-2 my-2'></div>}
          {shippingMethodMappings?.length
            ? shippingMethodMappings?.map((mapping, index) => {
                if (!mapping) return null
                const hasMultipleMappingRows = (shippingMethodMappings?.length ?? 0) > 1
                const reserveDeleteColumn =
                  hasMultipleMappingRows &&
                  !field_mapping.isDefault &&
                  platformFields.sourcePlatformField !== null
                const pairedDestinationCol =
                  platformFields.sourcePlatformField === null || field_mapping.isDefault
                    ? ''
                    : reserveDeleteColumn
                      ? 'col-sm-4'
                      : 'col-sm-5'
                const showRowLabels = index === 0
                return (
                  <div key={'mapping' + index + mapping?.id + ''} className={`mb-sm-0 mb-2 ${field_mapping.isDefault === false && platformFields?.sourcePlatformField !== null ? 'p-2' : '' }`}>
                    <div className='row'>
                      {platformFields.sourcePlatformField && !field_mapping?.isDefault ? (
                        platformFields.sourcePlatformField?.input_type === 'SELECT' ? (
                          <div className='col-12 col-sm-5'>
                            <div>
                              <label
                                htmlFor={`primaryStoreShippingMethod_${index}`}
                                className={clsx(
                                  'form-check-label',
                                  field_mapping?.isRequired ? 'required' : '',
                                  !showRowLabels && 'invisible'
                                )}
                                aria-hidden={!showRowLabels}
                              >
                                {platformFields?.sourcePlatformField !== null &&
                                  `${formatSettingDisplayText(field_mapping?.display_name)} from ${sourcePlatform}`}
                              </label>

                              <PlatformFieldDataSearchSelect
                                field={buildPlatformSearchField(
                                  platformFields.sourcePlatformField,
                                  shipmentMappingFieldData?.sourcePlatformFieldData ?? []
                                )}
                                className="react-select mt-2"
                                classNamePrefix="select"
                                inputId={`source-platform-${index}`}
                                instanceId={`source-platform-${index}`}
                                isSearchable
                                isClearable
                                options={
                                  shipmentMappingFieldData?.sourcePlatformFieldData?.map(
                                    (item: platformfielddata_set) => ({
                                      value: item.id,
                                      label: item.api_name,
                                    })
                                  ) ?? []
                                }
                                value={toPlatformFieldSelectValue(
                                  mapping?.source_platform_data_id,
                                  shipmentMappingFieldData?.sourcePlatformFieldData ?? []
                                )}
                                onChange={(selectedOption: any) => {
                                  if (selectedOption?.raw) {
                                    appendShipmentFieldData('source', selectedOption.raw)
                                  }

                                  const isCleared = selectedOption === null;
                                  const value = selectedOption?.value ?? "";

                                  validateField(
                                    index,
                                    "source_platform_data_id",
                                    value,
                                    field_mapping?.isRequired
                                  );

                                  let shadowArrayMapping = [...shippingMethodMappings];

                                  if (isCleared) {
                                    shadowArrayMapping[index] = createClearedMapping(mapping?.id ?? null);
                                  } else {
                                    let val: ShippingMethodMapping = {
                                      id: mapping?.id ? mapping?.id : null,
                                      source_platform_data_id: value,
                                      destination_platform_data_str: "",
                                      source_platform_data_str: "",
                                      hasPair: true,
                                    };

                                    if (platformFields.destinationPlatformField?.input_type === "SELECT") {
                                      if (!field_mapping.isDefault) {
                                        val.destination_platform_data_id = mapping.destination_platform_data_id;
                                      } else {
                                        val.destination_platform_data_id = null;
                                      }
                                    } else {
                                      if (!field_mapping?.isDefault) {
                                        val.destination_platform_data_str = mapping.destination_platform_data_str;
                                      }
                                    }

                                    shadowArrayMapping[index] = val;
                                  }

                                  let shadowFieldMappings = [...fieldMappings];
                                  let existingFieldMapping = shadowFieldMappings.find(
                                    (item) =>
                                      item.fieldCode === field_mapping.field_code &&
                                      item.groupCode === settingName
                                  );
                                  let existingFieldMappingIndex = shadowFieldMappings.findIndex(
                                    (item) =>
                                      item.fieldCode === field_mapping.field_code &&
                                      item.groupCode === settingName
                                  );

                                  if (existingFieldMapping) {
                                    existingFieldMapping.fieldMappingDataSet = shadowArrayMapping;
                                    shadowFieldMappings[existingFieldMappingIndex] = existingFieldMapping;
                                    setFieldMappings(shadowFieldMappings);
                                  } else {
                                    setFieldMappings((pre) => [
                                      ...pre,
                                      {
                                        groupCode: settingName,
                                        fieldCode: field_mapping.field_code,
                                        savedSettingId: settingId,
                                        savedMappingId: savedMapping?.id!,
                                        destinationFieldId:
                                          platformFields.destinationPlatformField?.id!,
                                        sourceFieldId:
                                          platformFields.sourcePlatformField?.id ?? null,
                                        fieldMappingDataSet: shadowArrayMapping,
                                      },
                                    ]);
                                  }

                                  setShippingMethodMappings([...shadowArrayMapping]);
                                }}
                              />

                              {errors[`${index}_source_platform_data_id`] && (
                                <small className="text-danger">
                                  {errors[`${index}_source_platform_data_id`]}
                                </small>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className='col-12 col-sm-5'>
                            <div className=''>
                              <label
                                htmlFor={`primaryStoreShippingMethod_${index}`}
                                className={clsx(
                                  'form-check-label',
                                  field_mapping?.isRequired ? 'required' : '',
                                  !showRowLabels && 'invisible'
                                )}
                                aria-hidden={!showRowLabels}
                              >
                                {platformFields?.sourcePlatformField !== null && `${formatSettingDisplayText(field_mapping?.display_name)} from ${sourcePlatform}`}
                              </label>
                              <input
                                type='text'
                                placeholder='Add a method'
                                className={`form-control form-control-lg mt-2 ${errors[`${index}_source_platform_data_str`] ? 'border border-danger': ''}`}
                                name={`mappings[${index}][source_platform_data_str]`}
                                value={mapping.source_platform_data_str}
                                required={field_mapping?.isRequired || false}
                                onChange={(ev) => {
                                  validateField(
                                    index,
                                    'source_platform_data_str',
                                    ev.target.value,
                                    field_mapping?.isRequired
                                  )
                                  let val: ShippingMethodMapping = {
                                    id: mapping.id ? mapping.id : null,
                                    source_platform_data_str: ev.target.value,
                                  }

                                  if (
                                    platformFields.destinationPlatformField?.input_type === 'SELECT'
                                  ) {
                                    if (!field_mapping.isDefault) {
                                      val.destination_platform_data_id =
                                        mapping.destination_platform_data_id
                                    }
                                  } else {
                                    if (!field_mapping?.isDefault) {
                                      val.destination_platform_data_str =
                                        mapping.destination_platform_data_str
                                    }
                                  }

                                  let shadowArrayMapping = [...shippingMethodMappings]
                                  shadowArrayMapping[index] = val

                                  let shadowFieldMappings = [...fieldMappings]
                                  let existingFieldMapping = shadowFieldMappings.find(
                                    (item) =>
                                      item.fieldCode === field_mapping.field_code &&
                                      item.groupCode === settingName
                                  )
                                  let existingFieldMappingIndex = shadowFieldMappings.findIndex(
                                    (item) =>
                                      item.fieldCode === field_mapping.field_code &&
                                      item.groupCode === settingName
                                  )

                                  if (existingFieldMapping) {
                                    existingFieldMapping.fieldMappingDataSet = shadowArrayMapping
                                    shadowFieldMappings[existingFieldMappingIndex] =
                                      existingFieldMapping
                                    setFieldMappings(shadowFieldMappings)
                                  } else {
                                    setFieldMappings((pre) => {
                                      return [
                                        ...pre,
                                        {
                                          groupCode: settingName,
                                          fieldCode: field_mapping.field_code,
                                          savedSettingId: settingId,
                                          savedMappingId: savedMapping?.id!,
                                          destinationFieldId:
                                            platformFields.destinationPlatformField?.id!,
                                          sourceFieldId:
                                            platformFields.sourcePlatformField?.id ?? null,
                                          fieldMappingDataSet: shadowArrayMapping,
                                        },
                                      ]
                                    })
                                  }

                                  setShippingMethodMappings([...shadowArrayMapping])
                                }}
                              />
                               {errors[`${index}_source_platform_data_str`] && (
                                <small className='text-danger'>
                                  {errors[`${index}_source_platform_data_str`]}
                                </small>
                              )}
                            </div>
                          </div>
                        )
                      ) : null}
                      {platformFields.sourcePlatformField && !field_mapping?.isDefault ? (
                        <div className='col-12 col-sm-1 my-2 my-sm-0'>
                          <div className='d-flex justify-content-center align-items-center h-100'>
                            <FontAwesomeIcon icon={faArrowRightArrowLeft} className='mt-8' />
                          </div>
                        </div>
                      ) : null}
                      {platformFields.destinationPlatformField ? (
                        platformFields.destinationPlatformField?.input_type === 'SELECT' ? (
                          <div
                            className={clsx(
                              'col-12',
                              pairedDestinationCol
                            )}
                          >
                            <div className="">
                              {!field_mapping.isDefault && platformFields?.sourcePlatformField !== null && (
                                <label
                                  htmlFor={`secondaryStoreShippingMethod_${index}`}
                                  className={clsx(
                                    'form-check-label',
                                    field_mapping?.isRequired ? 'required' : '',
                                    !showRowLabels && 'invisible'
                                  )}
                                  aria-hidden={!showRowLabels}
                                >
                                  {`${formatSettingDisplayText(field_mapping?.display_name)} from ${destinationPlatform}`}
                                </label>
                              )}

                              <PlatformFieldDataSearchSelect
                                field={buildPlatformSearchField(
                                  platformFields.destinationPlatformField,
                                  mergeSavedRowsIntoFieldData(
                                    shipmentMappingFieldData?.destinationPlatformFieldData ?? [],
                                    savedMapping?.fieldmappingdata_set ?? []
                                  )
                                )}
                                className={`react-select ${field_mapping.isDefault === true ? "" : "mt-2"}`}
                                classNamePrefix="select"
                                inputId={`destination-platform-${field_mapping?.field_code ?? field_mapping?.api_code ?? 'field'}-${index}`}
                                instanceId={`destination-platform-${field_mapping?.field_code ?? field_mapping?.api_code ?? 'field'}-${index}`}
                                isSearchable
                                isClearable
                                options={
                                  shipmentMappingFieldData?.destinationPlatformFieldData?.map(
                                    (item: platformfielddata_set) => ({
                                      value: item.id,
                                      label: item.api_name,
                                    })
                                  ) ?? []
                                }
                                value={toPlatformFieldSelectValue(
                                  mapping?.destination_platform_data_id,
                                  mergeSavedRowsIntoFieldData(
                                    shipmentMappingFieldData?.destinationPlatformFieldData ?? [],
                                    savedMapping?.fieldmappingdata_set ?? []
                                  )
                                )}
                                onChange={(selectedOption: any) => {
                                  if (selectedOption?.raw) {
                                    appendShipmentFieldData('destination', selectedOption.raw)
                                  }

                                  const isCleared = selectedOption === null;
                                  const value = selectedOption?.value ?? "";

                                  validateField(
                                    index,
                                    "destination_platform_data_id",
                                    value,
                                    field_mapping?.isRequired
                                  );

                                  let shadowArrayMapping = [...shippingMethodMappings];

                                  if (isCleared) {
                                    shadowArrayMapping[index] = createClearedMapping(mapping?.id ?? null);
                                  } else {
                                    let val: ShippingMethodMapping = {
                                      id: mapping?.id ? mapping?.id : null,
                                      destination_platform_data_id: value,
                                      destination_platform_data_str: "",
                                      source_platform_data_str: "",
                                      hasPair: true,
                                    };

                                    if (platformFields.sourcePlatformField?.input_type === "SELECT") {
                                      if (!field_mapping?.isDefault) {
                                        val.source_platform_data_id = mapping.source_platform_data_id;
                                      } else {
                                        val.source_platform_data_id = null;
                                      }
                                    } else {
                                      if (!field_mapping?.isDefault) {
                                        val.source_platform_data_str = mapping.source_platform_data_str;
                                      }
                                    }

                                    shadowArrayMapping[index] = val;
                                  }

                                  let shadowFieldMappings = [...fieldMappings];
                                  let existingFieldMapping = shadowFieldMappings.find(
                                    (item) =>
                                      item.fieldCode === field_mapping.field_code &&
                                      item.groupCode === settingName
                                  );
                                  let existingFieldMappingIndex = shadowFieldMappings.findIndex(
                                    (item) =>
                                      item.fieldCode === field_mapping.field_code &&
                                      item.groupCode === settingName
                                  );

                                  if (existingFieldMapping) {
                                    existingFieldMapping.fieldMappingDataSet = shadowArrayMapping;
                                    shadowFieldMappings[existingFieldMappingIndex] = existingFieldMapping;
                                    setFieldMappings(shadowFieldMappings);
                                  } else {
                                    setFieldMappings((pre) => [
                                      ...pre,
                                      {
                                        groupCode: settingName,
                                        fieldCode: field_mapping.field_code,
                                        savedSettingId: settingId,
                                        savedMappingId: savedMapping?.id!,
                                        destinationFieldId: platformFields.destinationPlatformField?.id!,
                                        sourceFieldId:
                                          platformFields.sourcePlatformField?.id ?? null,
                                        fieldMappingDataSet: shadowArrayMapping,
                                      },
                                    ]);
                                  }

                                  setShippingMethodMappings([...shadowArrayMapping]);
                                }}
                              />

                              {errors[`${index}_destination_platform_data_id`] && (
                                <small className="text-danger">
                                  {errors[`${index}_destination_platform_data_id`]}
                                </small>
                              )}
                            </div>
                          </div>
                        ) : platformFields.destinationPlatformField?.input_type ===
                          'MULTISELECT' ? (
                          <div
                            className={clsx(
                              'col-12',
                              pairedDestinationCol
                            )}
                          >
                            <div className=''>
                              {/* <label
                                htmlFor={`secondaryStoreShippingMethod_${index}`}
                                className='form-check-label'
                              >
                                {formatSettingDisplayText(field_mapping?.display_name)} from {destinationPlatform}
                              </label> */}
                              <PlatformFieldDataSearchSelect
                                field={buildPlatformSearchField(
                                  platformFields.destinationPlatformField,
                                  shipmentMappingFieldData?.destinationPlatformFieldData ?? []
                                )}
                                id='secondaryStoreShippingMethod'
                                inputId={`destination-multiselect-${index}`}
                                instanceId={`destination-multiselect-${index}`}
                                isMulti
                                options={shipmentMappingFieldData?.destinationPlatformFieldData?.map(
                                  (item: platformfielddata_set) => ({
                                    value: item.id,
                                    label: item.api_name,
                                  })
                                )}
                                value={toPlatformFieldMultiSelectValue(
                                  mapping?.destination_platform_data_id,
                                  shipmentMappingFieldData?.destinationPlatformFieldData ?? []
                                )}
                                onChange={(selectedOptions) => {
                                  if (selectedOptions?.length) {
                                    appendShipmentFieldData(
                                      'destination',
                                      selectedOptions.map((opt: any) => opt.raw).filter(Boolean)
                                    )
                                  }

                                  const selectedValues = Array.isArray(selectedOptions)
                                    ? selectedOptions.map((opt) => opt.value.toString())
                                    : null

                                  validateField(
                                    index,
                                    'destination_platform_data_id',
                                    selectedValues,
                                    field_mapping?.isRequired
                                  )
                                const selectedOptionIds = selectedValues && selectedValues.map((value) => {
                                    const selectedOptionIndex = mapping?.destination_platform_data_id.indexOf(value);
                                    return selectedOptionIndex !== -1 ? mapping?.id[selectedOptionIndex] : null;
                                });
                            
                                  let val: ShippingMethodMapping = {
                                    id: selectedOptionIds || null,
                                    destination_platform_data_id: selectedValues,
                                    destination_platform_data_str: '',
                                    source_platform_data_str: '',
                                  }

                                  if (platformFields.sourcePlatformField?.input_type === 'SELECT') {
                                    if (!field_mapping?.isDefault) {
                                      val.source_platform_data_id = mapping.source_platform_data_id
                                    } else {
                                      val.source_platform_data_id = null
                                    }
                                  } else {
                                    if (!field_mapping?.isDefault) {
                                      val.source_platform_data_str =
                                        mapping.source_platform_data_str
                                    }
                                  }
                                  let shadowArrayMapping: ShippingMethodMapping[] =
                                    val?.destination_platform_data_id?.length > 0
                                      ? val.destination_platform_data_id.map(
                                          (value: any, i: number) => ({
                                            destination_platform_data_id: value.toString(),
                                            source_platform_data_id: val.source_platform_data_id ?? null,
                                            id: val.id && val.id[i] ? Number(val.id[i]) : null,
                                            destination_platform_data_str: '',
                                            source_platform_data_str: val.source_platform_data_str ?? '',
                                          })
                                        )
                                      : []

                                  shadowArrayMapping = decorateDefaultMappingRows(shadowArrayMapping)
                                  let shadowFieldMappings = [...fieldMappings]
                                  let existingFieldMapping = shadowFieldMappings.find(
                                    (item) =>
                                      item.fieldCode === field_mapping.field_code &&
                                      item.groupCode === settingName
                                  )
                                  let existingFieldMappingIndex = shadowFieldMappings.findIndex(
                                    (item) =>
                                      item.fieldCode === field_mapping.field_code &&
                                      item.groupCode === settingName
                                  )

                                  if (existingFieldMapping) {
                                    existingFieldMapping.fieldMappingDataSet = shadowArrayMapping
                                    Object.assign(
                                      existingFieldMapping,
                                      getDefaultMappingEntryExtras()
                                    )
                                    shadowFieldMappings[existingFieldMappingIndex] =
                                      existingFieldMapping
                                    setFieldMappings(shadowFieldMappings)
                                  } else {
                                    setFieldMappings((pre) => [
                                      ...pre,
                                      {
                                        groupCode: settingName,
                                        fieldCode: field_mapping.field_code,
                                        savedSettingId: settingId,
                                        savedMappingId: savedMapping?.id!,
                                        destinationFieldId:
                                          platformFields.destinationPlatformField?.id!,
                                        sourceFieldId:
                                          platformFields.sourcePlatformField?.id ?? null,
                                        fieldMappingDataSet: shadowArrayMapping,
                                        ...getDefaultMappingEntryExtras(),
                                      },
                                    ])
                                  }

                                  setShippingMethodMappings([val])
                                }}
                                styles={{
                                  control: (base, state) => ({
                                    ...base,
                                    borderColor: errors[`${index}_destination_platform_data_id`] ? "crimson" : base.borderColor,
                                    boxShadow: state.isFocused
                                      ? errors[`${index}_destination_platform_data_id`] 
                                        ? "0 0 0 1px crimson" 
                                        : "0 0 0 1px #2684FF"
                                      : "none",
                                    "&:hover": {
                                      borderColor: errors[`${index}_destination_platform_data_id`] ? "crimson" : base.borderColor,
                                    },
                                  }),
                                }}
                                classNamePrefix='react-select'
                              />
                               {errors[`${index}_destination_platform_data_id`] && (
                                <small className='text-danger'>
                                  {errors[`${index}_destination_platform_data_id`]}
                                </small>
                              )}
                            </div>
                          </div>
                        ) : platformFields.destinationPlatformField?.input_type === 'BOOLEAN' ? (
                          <div
                            className={clsx(
                              'col-12',
                              pairedDestinationCol
                            )}
                          >
                            <div className=''>
                              {/* <label
                                htmlFor={`secondaryStoreShippingMethod_${index}`}
                                className='form-check-label'
                              >
                                {formatSettingDisplayText(field_mapping?.display_name)} from {destinationPlatform}
                              </label> */}
                              <div className='form-check form-switch form-check-solid'>
                                <input
                                  type='checkbox'
                                  className='form-check-input h-30px w-50px'
                                  name={`mappings[${index}][destination_platform_data_str]`}
                                  checked={mapping.destination_platform_data_str === 'true'}
                                  required={field_mapping?.isRequired || false}
                                  onChange={(ev) => {
                                    validateField(
                                      index,
                                      'destination_platform_data_str',
                                      ev.target.value,
                                      field_mapping?.isRequired
                                    )

                                    let val: ShippingMethodMapping = {
                                      id: mapping.id ? mapping.id : null,
                                      destination_platform_data_str: ev.target.checked
                                        ? 'true'
                                        : 'false',
                                    }

                                    if (
                                      platformFields.sourcePlatformField?.input_type === 'SELECT'
                                    ) {
                                      if (!field_mapping?.isDefault) {
                                        val.source_platform_data_id =
                                          mapping.source_platform_data_id
                                      }
                                    } else {
                                      if (!field_mapping?.isDefault) {
                                        val.source_platform_data_str =
                                          mapping.source_platform_data_str
                                      }
                                    }

                                    let shadowArrayMapping = [...shippingMethodMappings]
                                    shadowArrayMapping[index] = val

                                    let shadowFieldMappings = [...fieldMappings]
                                    let existingFieldMapping = shadowFieldMappings.find(
                                      (item) =>
                                        item.fieldCode === field_mapping.field_code &&
                                        item.groupCode === settingName
                                    )
                                    let existingFieldMappingIndex = shadowFieldMappings.findIndex(
                                      (item) =>
                                        item.fieldCode === field_mapping.field_code &&
                                        item.groupCode === settingName
                                    )

                                    if (existingFieldMapping) {
                                      existingFieldMapping.fieldMappingDataSet = shadowArrayMapping
                                      shadowFieldMappings[existingFieldMappingIndex] =
                                        existingFieldMapping
                                      setFieldMappings(shadowFieldMappings)
                                    } else {
                                      setFieldMappings((pre) => {
                                        return [
                                          ...pre,
                                          {
                                            groupCode: settingName,
                                            fieldCode: field_mapping.field_code,
                                            savedSettingId: settingId,
                                            savedMappingId: savedMapping?.id!,
                                            destinationFieldId:
                                              platformFields.destinationPlatformField?.id!,
                                            sourceFieldId:
                                              platformFields.sourcePlatformField?.id ?? null,
                                            fieldMappingDataSet: shadowArrayMapping,
                                          },
                                        ]
                                      })
                                    }
                                    setShippingMethodMappings([...shadowArrayMapping])
                                  }}
                                />
                                 {errors[`${index}_destination_platform_data_id`] && (
                                <small className='text-danger'>
                                  {errors[`${index}_destination_platform_data_id`]}
                                </small>
                              )}
                              </div>
                            </div>
                          </div>
                        ) : platformFields.destinationPlatformField?.input_type === 'DATETIME' ? (
                          <div
                            className={clsx(
                              'col-12',
                              pairedDestinationCol
                            )}
                          >
                            <div className=''>
                              {/* <label
                                htmlFor={`secondaryStoreShippingMethod_${index}`}
                                className='form-check-label'
                              >
                                {formatSettingDisplayText(field_mapping?.display_name)} from {destinationPlatform}
                              </label> */}
                              <input
                                type='datetime-local'
                                placeholder='Add a method'
                                className={`form-control form-control-lg ${errors[`${index}_destination_platform_data_str`] ? 'border border-danger': ''}`}
                                name={`mappings[${index}][destination_platform_data_str]`}
                                value={
                                  formatDateString(mapping?.destination_platform_data_str ?? '') ||
                                  ''
                                }
                                required={field_mapping?.isRequired || false}
                                onChange={(ev) => {
                                  validateField(
                                    index,
                                    'destination_platform_data_str',
                                    ev.target.value,
                                    field_mapping?.isRequired
                                  )

                                  let val: ShippingMethodMapping = {
                                    id: mapping.id ? mapping.id : null,
                                    destination_platform_data_str: ev.target.value,
                                  }

                                  if (platformFields.sourcePlatformField?.input_type === 'SELECT') {
                                    if (!field_mapping?.isDefault) {
                                      val.source_platform_data_id = mapping.source_platform_data_id
                                    }
                                  } else {
                                    if (!field_mapping?.isDefault) {
                                      val.source_platform_data_str =
                                        mapping.source_platform_data_str
                                    }
                                  }

                                  let shadowArrayMapping = [...shippingMethodMappings]
                                  shadowArrayMapping[index] = val

                                  let shadowFieldMappings = [...fieldMappings]
                                  let existingFieldMapping = shadowFieldMappings.find(
                                    (item) =>
                                      item.fieldCode === field_mapping.field_code &&
                                      item.groupCode === settingName
                                  )
                                  let existingFieldMappingIndex = shadowFieldMappings.findIndex(
                                    (item) =>
                                      item.fieldCode === field_mapping.field_code &&
                                      item.groupCode === settingName
                                  )

                                  if (existingFieldMapping) {
                                    existingFieldMapping.fieldMappingDataSet = shadowArrayMapping
                                    shadowFieldMappings[existingFieldMappingIndex] =
                                      existingFieldMapping
                                    setFieldMappings(shadowFieldMappings)
                                  } else {
                                    setFieldMappings((pre) => {
                                      return [
                                        ...pre,
                                        {
                                          groupCode: settingName,
                                          fieldCode: field_mapping.field_code,
                                          savedSettingId: settingId,
                                          savedMappingId: savedMapping?.id!,
                                          destinationFieldId:
                                            platformFields.destinationPlatformField?.id!,
                                          sourceFieldId:
                                            platformFields.sourcePlatformField?.id ?? null,
                                          fieldMappingDataSet: shadowArrayMapping,
                                        },
                                      ]
                                    })
                                  }
                                  setShippingMethodMappings([...shadowArrayMapping])
                                }}
                              />
                               {errors[`${index}_destination_platform_data_str`] && (
                                <small className='text-danger'>
                                  {errors[`${index}_destination_platform_data_str`]}
                                </small>
                              )}
                            </div>
                          </div>
                        ) : platformFields.destinationPlatformField?.input_type === 'INTEGER' ? (
                          <div
                            className={clsx(
                              'col-12',
                              pairedDestinationCol
                            )}
                          >
                            <div className=''>
                              {/* <label
                                htmlFor={`secondaryStoreShippingMethod_${index}`}
                                className='form-check-label'
                              >
                                {formatSettingDisplayText(field_mapping?.display_name)} from {destinationPlatform}
                              </label> */}
                              <input
                                type='number'
                                placeholder='Add amount'
                                className={`form-control form-control-lg ${errors[`${index}_destination_platform_data_str`] ? 'border border-danger': ''}`}
                                name={`mappings[${index}][destination_platform_data_str]`}
                                value={mapping.destination_platform_data_str}
                                required={field_mapping?.isRequired || false}
                                onChange={(ev) => {
                                  validateField(
                                    index,
                                    'destination_platform_data_str',
                                    ev.target.value,
                                    field_mapping?.isRequired
                                  )

                                  let val: ShippingMethodMapping = {
                                    id: mapping.id ? mapping.id : null,
                                    destination_platform_data_str: ev.target.value,
                                  }

                                  if (platformFields.sourcePlatformField?.input_type === 'SELECT') {
                                    if (!field_mapping?.isDefault) {
                                      val.source_platform_data_id = mapping.source_platform_data_id
                                    }
                                  } else {
                                    if (!field_mapping?.isDefault) {
                                      val.source_platform_data_str =
                                        mapping.source_platform_data_str
                                    }
                                  }

                                  let shadowArrayMapping = [...shippingMethodMappings]
                                  shadowArrayMapping[index] = val

                                  let shadowFieldMappings = [...fieldMappings]
                                  let existingFieldMapping = shadowFieldMappings.find(
                                    (item) =>
                                      item.fieldCode === field_mapping.field_code &&
                                      item.groupCode === settingName
                                  )
                                  let existingFieldMappingIndex = shadowFieldMappings.findIndex(
                                    (item) =>
                                      item.fieldCode === field_mapping.field_code &&
                                      item.groupCode === settingName
                                  )

                                  if (existingFieldMapping) {
                                    existingFieldMapping.fieldMappingDataSet = shadowArrayMapping
                                    shadowFieldMappings[existingFieldMappingIndex] =
                                      existingFieldMapping
                                    setFieldMappings(shadowFieldMappings)
                                  } else {
                                    setFieldMappings((pre) => {
                                      return [
                                        ...pre,
                                        {
                                          groupCode: settingName,
                                          fieldCode: field_mapping.field_code,
                                          savedSettingId: settingId,
                                          savedMappingId: savedMapping?.id!,
                                          destinationFieldId:
                                            platformFields.destinationPlatformField?.id!,
                                          sourceFieldId:
                                            platformFields.sourcePlatformField?.id ?? null,
                                          fieldMappingDataSet: shadowArrayMapping,
                                        },
                                      ]
                                    })
                                  }
                                  setShippingMethodMappings([...shadowArrayMapping])
                                }}
                              />
                               {errors[`${index}_destination_platform_data_str`] && (
                                <small className='text-danger'>
                                  {errors[`${index}_destination_platform_data_str`]}
                                </small>
                              )}
                            </div>
                          </div>
                        ) : platformFields.destinationPlatformField?.input_type === 'TEXTAREA' ? (
                          <div
                            className={clsx(
                              'col-12',
                              pairedDestinationCol
                            )}
                          >
                            <div className=''>
                              {/* <label
                                htmlFor={`secondaryStoreShippingMethod_${index}`}
                                className='form-check-label'
                              >
                                {formatSettingDisplayText(field_mapping?.display_name)} from {destinationPlatform}
                              </label> */}
                              <textarea
                                placeholder='Add a description'
                                className={`form-control form-control-lg ${errors[`${index}_destination_platform_data_str`] ? 'border border-danger': ''}`}
                                rows={2}
                                name={`mappings[${index}][destination_platform_data_str]`}
                                value={mapping.destination_platform_data_str}
                                required={field_mapping?.isRequired || false}
                                onChange={(ev) => {
                                  validateField(
                                    index,
                                    'destination_platform_data_str',
                                    ev.target.value,
                                    field_mapping?.isRequired
                                  )

                                  let val: ShippingMethodMapping = {
                                    id: mapping.id ? mapping.id : null,
                                    destination_platform_data_str: ev.target.value,
                                  }

                                  if (platformFields.sourcePlatformField?.input_type === 'SELECT') {
                                    if (!field_mapping?.isDefault) {
                                      val.source_platform_data_id = mapping.source_platform_data_id
                                    }
                                  } else {
                                    if (!field_mapping?.isDefault) {
                                      val.source_platform_data_str =
                                        mapping.source_platform_data_str
                                    }
                                  }

                                  let shadowArrayMapping = [...shippingMethodMappings]
                                  shadowArrayMapping[index] = val

                                  let shadowFieldMappings = [...fieldMappings]
                                  let existingFieldMapping = shadowFieldMappings.find(
                                    (item) =>
                                      item.fieldCode === field_mapping.field_code &&
                                      item.groupCode === settingName
                                  )
                                  let existingFieldMappingIndex = shadowFieldMappings.findIndex(
                                    (item) =>
                                      item.fieldCode === field_mapping.field_code &&
                                      item.groupCode === settingName
                                  )

                                  if (existingFieldMapping) {
                                    existingFieldMapping.fieldMappingDataSet = shadowArrayMapping
                                    shadowFieldMappings[existingFieldMappingIndex] =
                                      existingFieldMapping
                                    setFieldMappings(shadowFieldMappings)
                                  } else {
                                    setFieldMappings((pre) => {
                                      return [
                                        ...pre,
                                        {
                                          groupCode: settingName,
                                          fieldCode: field_mapping.field_code,
                                          savedSettingId: settingId,
                                          savedMappingId: savedMapping?.id!,
                                          destinationFieldId:
                                            platformFields.destinationPlatformField?.id!,
                                          sourceFieldId:
                                            platformFields.sourcePlatformField?.id ?? null,
                                          fieldMappingDataSet: shadowArrayMapping,
                                        },
                                      ]
                                    })
                                  }
                                  setShippingMethodMappings([...shadowArrayMapping])
                                }}
                              />
                               {errors[`${index}_destination_platform_data_str`] && (
                                <small className='text-danger'>
                                  {errors[`${index}_destination_platform_data_str`]}
                                </small>
                              )}
                              {shouldShowFieldMappingShortcodes(field_mapping) ? (
                                <MappingShortcodeChips
                                  platformId={selectedPlatformId}
                                  settingName={resolveFieldMappingShortcodeSettingName(field_mapping)}
                                  shortcodeOptions={field_mapping?.shortcode_options}
                                />
                              ) : null}
                            </div>
                          </div>
                        ) : (
                          <div
                            className={clsx(
                              'col-12',
                              pairedDestinationCol
                            )}
                          >
                            <div className=''>
                              {!field_mapping.isDefault && platformFields?.sourcePlatformField !== null && (
                                <label
                                  htmlFor={`secondaryStoreShippingMethod_${index}`}
                                  className={clsx(
                                    'form-check-label',
                                    field_mapping?.isRequired ? 'required' : '',
                                    !showRowLabels && 'invisible'
                                  )}
                                  aria-hidden={!showRowLabels}
                                >
                                  {platformFields?.sourcePlatformField !== null &&
                                    `${formatSettingDisplayText(field_mapping?.display_name)} from ${destinationPlatform}`}
                                </label>
                              )}
                              <input
                                type='text'
                                placeholder='Add a method'
                                className={`form-control form-control-lg ${errors[`${index}_destination_platform_data_str`] ? 'border border-danger': ''} ${field_mapping.isDefault === true ? '' : 'mt-2'}`}
                                name={`mappings[${index}][destination_platform_data_str]`}
                                value={mapping.destination_platform_data_str}
                                required={field_mapping?.isRequired || false}
                                onChange={(ev) => {
                                  validateField(
                                    index,
                                    'destination_platform_data_str',
                                    ev.target.value,
                                    field_mapping?.isRequired
                                  )

                                  let val: ShippingMethodMapping = {
                                    id: mapping.id ? mapping.id : null,
                                    destination_platform_data_str: ev.target.value,
                                  }

                                  if (platformFields.sourcePlatformField?.input_type === 'SELECT') {
                                    if (!field_mapping?.isDefault) {
                                      val.source_platform_data_id = mapping.source_platform_data_id
                                    }
                                  } else {
                                    if (!field_mapping?.isDefault) {
                                      val.source_platform_data_str =
                                        mapping.source_platform_data_str
                                    }
                                  }

                                  let shadowArrayMapping = [...shippingMethodMappings]
                                  shadowArrayMapping[index] = val

                                  let shadowFieldMappings = [...fieldMappings]
                                  let existingFieldMapping = shadowFieldMappings.find(
                                    (item) =>
                                      item.fieldCode === field_mapping.field_code &&
                                      item.groupCode === settingName
                                  )
                                  let existingFieldMappingIndex = shadowFieldMappings.findIndex(
                                    (item) =>
                                      item.fieldCode === field_mapping.field_code &&
                                      item.groupCode === settingName
                                  )

                                  if (existingFieldMapping) {
                                    existingFieldMapping.fieldMappingDataSet = shadowArrayMapping
                                    shadowFieldMappings[existingFieldMappingIndex] =
                                      existingFieldMapping
                                    setFieldMappings(shadowFieldMappings)
                                  } else {
                                    setFieldMappings((pre) => {
                                      return [
                                        ...pre,
                                        {
                                          groupCode: settingName,
                                          fieldCode: field_mapping.field_code,
                                          savedSettingId: settingId,
                                          savedMappingId: savedMapping?.id!,
                                          destinationFieldId:
                                            platformFields.destinationPlatformField?.id!,
                                          sourceFieldId:
                                            platformFields.sourcePlatformField?.id ?? null,
                                          fieldMappingDataSet: shadowArrayMapping,
                                        },
                                      ]
                                    })
                                  }
                                  setShippingMethodMappings([...shadowArrayMapping])
                                }}
                              />
                              {errors[`${index}_destination_platform_data_str`] && (
                                <small className='text-danger'>
                                  {errors[`${index}_destination_platform_data_str`]}
                                </small>
                              )}
                              {shouldShowFieldMappingShortcodes(field_mapping) ? (
                                <MappingShortcodeChips
                                  platformId={selectedPlatformId}
                                  settingName={resolveFieldMappingShortcodeSettingName(field_mapping)}
                                  shortcodeOptions={field_mapping?.shortcode_options}
                                />
                              ) : null}
                            </div>
                          </div>
                        )
                      ) : null}
                      {reserveDeleteColumn ? (
                        <div className='col-auto mb-2 d-flex align-items-end mapping-row-delete'>
                          {index !== 0 && isDelete ? (
                            <button
                              type='button'
                              onClick={() => {
                                let copyMppings = [...shippingMethodMappings]
                                if (mapping.id) {
                                  dispatch(
                                    deleteFieldMappingAsync({mappingDataId: mapping.id})
                                  ).then((response) => {
                                    successToast(response.payload.message)

                                    if (savedMapping) {
                                      setLoadingMappingFields(true)
                                      dispatch(
                                        getSavedFieldMappingsDataAsyncByMappingId({
                                          mappingId: savedMapping.id,
                                        })
                                      ).then((response) => {
                                        if (response?.payload?.fieldmappingdata_set?.length) {
                                          const mappings =
                                            response?.payload?.fieldmappingdata_set.map((item) => ({
                                              id: item?.id,
                                              destination_platform_data_id: item
                                                ?.destination_platform_data?.id
                                                ? item?.destination_platform_data?.id + ''
                                                : '',
                                              source_platform_data_id: item
                                                ?.destination_platform_data?.id
                                                ? item?.source_platform_data?.id + ''
                                                : '',
                                              destination_platform_data_str:
                                                item.destination_platform_data_str ?? '',
                                              source_platform_data_str:
                                                item.source_platform_data_str ?? '',
                                            }))
                                          setShippingMethodMappings(mappings)
                                          let shadowFieldMappings = [...fieldMappings]
                                          let existingFieldMapping = shadowFieldMappings.find(
                                            (item) =>
                                              item.fieldCode === field_mapping.field_code &&
                                              item.groupCode === settingName
                                          )
                                          let existingFieldMappingIndex =
                                            shadowFieldMappings.findIndex(
                                              (item) =>
                                                item.fieldCode === field_mapping.field_code &&
                                                item.groupCode === settingName
                                            )

                                          if (existingFieldMapping) {
                                            existingFieldMapping.fieldMappingDataSet = mappings
                                            shadowFieldMappings[existingFieldMappingIndex] =
                                              existingFieldMapping
                                            setFieldMappings(shadowFieldMappings)
                                          }
                                        }
                                        setLoadingMappingFields(false)
                                      })
                                    }
                                  })
                                } else {
                                  let filteredMappings = copyMppings.filter((it, ind) => {
                                    if (ind !== index) {
                                      return it
                                    }
                                    return false
                                  })
                                  setShippingMethodMappings([...filteredMappings])
                                }
                              }}
                              className='btn btn-sm'
                              style={{color: 'red'}}
                            >
                              <FontAwesomeIcon icon={faDeleteLeft} style={{fontSize: '20px'}} />
                            </button>
                          ) : (
                            <span className='btn btn-sm invisible user-select-none' aria-hidden='true'>
                              <FontAwesomeIcon icon={faDeleteLeft} style={{fontSize: '20px'}} />
                            </span>
                          )}
                        </div>
                      ) : null}
                    </div>
                  </div>
                )
              })
            : null}
          {platformFields.sourcePlatformField && !field_mapping?.isDefault ? (
            <div className='d-flex justify-content-end'>
              <button
                className='btn btn-primary btn-sm me-3'
                type='button'
                onClick={() => {
                  const newItem = createEmptyShippingMethodMapping()
                  if (platformFields.sourcePlatformField?.input_type === 'SELECT') {
                    newItem.source_platform_data_id = ''
                  } else {
                    newItem.source_platform_data_str = ''
                  }

                  if (platformFields.destinationPlatformField?.input_type === 'SELECT') {
                    newItem.destination_platform_data_id = ''
                  } else {
                    newItem.destination_platform_data_str = ''
                  }

                  let shadowArrayMapping = [...shippingMethodMappings]
                  shadowArrayMapping.push(newItem)

                  const existingFieldMappingIndex = fieldMappings.findIndex(
                    (item) =>
                      item.fieldCode === field_mapping.field_code &&
                      item.groupCode === settingName
                  )

                  if (existingFieldMappingIndex !== -1) {
                    const updatedFieldMappings = fieldMappings.map((item, idx) =>
                      idx === existingFieldMappingIndex
                        ? {
                            ...item,
                            fieldMappingDataSet: shadowArrayMapping,
                          }
                        : item
                    )
                    setFieldMappings(updatedFieldMappings)
                  } else {
                    setFieldMappings((pre) => {
                      return [
                        ...pre,
                        {
                          groupCode: settingName,
                          fieldCode: field_mapping.field_code,
                          savedSettingId: settingId,
                          savedMappingId: savedMapping?.id!,
                          destinationFieldId: platformFields.destinationPlatformField?.id!,
                          sourceFieldId: platformFields.sourcePlatformField?.id ?? null,
                          fieldMappingDataSet: shadowArrayMapping,
                        },
                      ]
                    })
                  }
                  setShippingMethodMappings(shadowArrayMapping)
                }}
              >
                <FontAwesomeIcon icon={faPlus} />
              </button>{' '}
            </div>
          ) : null}

          {/* { platformFields.sourcePlatformField || platformFields.destinationPlatformField ? 
                  <div className='d-flex justify-content-end'>
                    <button
                      type='button'
                      form='mapping_form'
                      className='btn btn-success btn-sm mt-2 me-3 align-self-end'
                      onClick={() => !platformFields.sourcePlatformField ? handleSingleValues(values.mappings, setSubmitting):   handleSaveShipmentMappings(values.mappings, setSubmitting)}
                    >
                      {loadingMappingSave || loadingLocationOption? 'Saving...' : 'Save Mapping'}
                    </button>
                  </div> : null } */}
        </div>
      </div>
    </div>
  )
}

export default MappingInput
